// ABOUTME: ProseMirror plugin that shows an Anni icon on text selections
// ABOUTME: Clicking the icon sends the selected text to the Anni chat
import { Plugin, PluginKey } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { Note } from '@annote/types';
import { colors, text } from '@annote/core/styles';

const anniChatSelectorPluginKey = new PluginKey('anniChatSelectorPlugin');

interface AnniChatHandler {
  insertReference: (noteId: string, noteName: string, selectedText: string, textStart: number, textEnd: number) => void;
}

let globalAnniChatHandler: AnniChatHandler | null = null;

export const setAnniChatHandler = (handler: AnniChatHandler): void => {
  globalAnniChatHandler = handler;
};

const hideAnniMenu = (): void => {
  const menu = document.getElementById('anni-selector-menu');
  if (menu) {
    menu.style.display = 'none';
  }
};

const handleAnniClick = (
  noteId: string,
  noteName: string,
  selectedText: string,
  textStart: number,
  textEnd: number,
): void => {
  if (globalAnniChatHandler) {
    globalAnniChatHandler.insertReference(noteId, noteName, selectedText, textStart, textEnd);
  }
  hideAnniMenu();
};

const showAnniMenu = (
  rect: { left: number; right: number; top: number; bottom: number },
  noteId: string,
  noteName: string,
  selectedText: string,
  textStart: number,
  textEnd: number,
): void => {
  let menu = document.getElementById('anni-selector-menu');
  if (!menu) {
    // Create the menu if it doesn't exist
    menu = document.createElement('div');
    menu.id = 'anni-selector-menu';
    menu.setAttribute('data-testid', 'anni-selector');

    // Create the SVG icon (from ai.svg)
    // eslint-disable-next-line max-len
    const svgPath =
      '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_16036_3493)"><path d="M6.62821 10.3346C6.56869 10.1039 6.44843 9.89337 6.27995 9.72489C6.11147 9.55641 5.90092 9.43616 5.67021 9.37664L1.58021 8.32197C1.51043 8.30217 1.44901 8.26014 1.40528 8.20227C1.36155 8.1444 1.33789 8.07384 1.33789 8.00131C1.33789 7.92877 1.36155 7.85821 1.40528 7.80034C1.44901 7.74247 1.51043 7.70044 1.58021 7.68064L5.67021 6.62531C5.90084 6.56584 6.11134 6.44569 6.27981 6.27733C6.44828 6.10898 6.56858 5.89856 6.62821 5.66797L7.68287 1.57797C7.70248 1.50792 7.74446 1.4462 7.80242 1.40224C7.86038 1.35827 7.93113 1.33447 8.00387 1.33447C8.07662 1.33447 8.14737 1.35827 8.20533 1.40224C8.26328 1.4462 8.30527 1.50792 8.32487 1.57797L9.37887 5.66797C9.43839 5.89869 9.55865 6.10924 9.72713 6.27772C9.89561 6.4462 10.1062 6.56645 10.3369 6.62597L14.4269 7.67997C14.4972 7.69937 14.5592 7.74131 14.6034 7.79936C14.6476 7.8574 14.6716 7.92835 14.6716 8.00131C14.6716 8.07426 14.6476 8.14521 14.6034 8.20325C14.5592 8.2613 14.4972 8.30324 14.4269 8.32264L10.3369 9.37664C10.1062 9.43616 9.89561 9.55641 9.72713 9.72489C9.55865 9.89337 9.43839 10.1039 9.37887 10.3346L8.32421 14.4246C8.3046 14.4947 8.26262 14.5564 8.20466 14.6004C8.1467 14.6443 8.07595 14.6681 8.00321 14.6681C7.93046 14.6681 7.85971 14.6443 7.80175 14.6004C7.7438 14.5564 7.70181 14.4947 7.68221 14.4246L6.62821 10.3346Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/><path d="M13.3369 2.00146V4.66813" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/><path d="M14.6702 3.33496H12.0035" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/><path d="M2.67023 11.335V12.6683" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/><path d="M3.33687 12.0015H2.00354" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_16036_3493"><rect width="16" height="16" fill="white" transform="translate(0.00354004 0.00146484)"/></clipPath></defs></svg>';
    menu.innerHTML = svgPath;

    // Apply styles inline
    menu.style.position = 'absolute';
    menu.style.backgroundColor = colors.bg.primary;
    menu.style.border = `1px solid ${colors.borders.input.primary}`;
    menu.style.padding = '8px';
    menu.style.borderRadius = '6px';
    menu.style.fontFamily = text.family.primary;
    menu.style.color = colors.text.primary;
    menu.style.fontSize = text.size.small;
    menu.style.boxShadow = '0px 3px 6px rgba(15, 15, 15, 0.1)';
    menu.style.cursor = 'pointer';
    menu.style.zIndex = '1010';
    menu.style.display = 'flex';
    menu.style.alignItems = 'center';
    menu.style.justifyContent = 'center';
    menu.style.width = '32px';
    menu.style.height = '32px';
    menu.style.transition = 'all 0.2s ease';

    // Add hover effect
    menu.addEventListener('mouseenter', () => {
      menu!.style.boxShadow = colors.shadow.card.hover;
      menu!.style.transform = 'scale(1.05)';
    });

    menu.addEventListener('mouseleave', () => {
      menu!.style.boxShadow = '0px 3px 6px rgba(15, 15, 15, 0.1)';
      menu!.style.transform = 'scale(1)';
    });

    document.body.appendChild(menu);
  }

  // Position the menu at the bottom-right of the selected text
  menu.style.left = `${rect.right + 4}px`; // 4px offset from the right edge
  menu.style.top = `${rect.bottom + 4}px`; // 4px offset from the bottom
  menu.style.display = 'flex';

  // Update click handler with current note and selection
  menu.onclick = () => {
    handleAnniClick(noteId, noteName, selectedText, textStart, textEnd);
  };
};

export const anniChatSelectorPlugin = (options: { note: Note }): Plugin =>
  new Plugin({
    key: anniChatSelectorPluginKey,
    view: () => ({
      update: (view: EditorView) => {
        const { state } = view;
        const { from, to } = state.selection;

        // Only show menu if there's a non-empty selection
        if (from !== to) {
          const selectedText = state.doc.textBetween(from, to, ' ');

          // Only show if there's actual text (not just whitespace)
          if (selectedText.trim().length > 0) {
            // Calculate character positions in the note's full text
            // Note: We need to account for ProseMirror's position system which includes node boundaries
            // For a document like <doc><p>text</p></doc>, position 0 is before <doc>,
            // 1 is before <p>, 2 is start of text
            // We want character positions in the actual text content,
            // so we get text from position 1 (start of content)
            const docStart = 1; // Skip the opening doc node
            const textBeforeSelection = state.doc.textBetween(docStart, from, ' ');
            const textStart = textBeforeSelection.length;
            const textEnd = textStart + selectedText.length;

            // Get the coordinates at the end of the selection
            const endCoords = view.coordsAtPos(to);
            showAnniMenu(
              endCoords,
              options.note.id,
              options.note.title || 'Untitled Note',
              selectedText,
              textStart,
              textEnd,
            );
            return;
          }
        }

        hideAnniMenu();
      },
    }),
    props: {
      handleDOMEvents: {
        // Hide menu when clicking outside
        mousedown: (view, event) => {
          const menu = document.getElementById('anni-selector-menu');
          if (menu && !menu.contains(event.target as Node)) {
            // Don't hide immediately to allow the click to register
            setTimeout(() => {
              const { from, to } = view.state.selection;
              if (from === to) {
                hideAnniMenu();
              }
            }, 0);
          }
          return false;
        },
      },
    },
  });
